// Installation over Bluetooth — the panel (2026-10-18). Logic: core/install/bleInstall.
//
//   Owner ▾   Location ▾   Default room ▾  [+ room]   Wi-Fi name / password
//   ☑ Show only unassigned
//   ☑ D4F98D14B0A0  Wwh Dimmable (25)  [name……]  [room ▾]  [Identify]   ✓ assigned · ✓ Wi-Fi · ● online
//   [Install 25]  [Stop]
//
// `mode`:
//   'admin' — Super Admin on the BLE bench: any owner, any of their locations.
//   'owner' — (next stage, not routed yet) the signed-in user installs their own
//             new boards: owner = themself, their own locations only, and no
//             other person's details are shown.
//
// ⚠️ A ROW THAT IS ASSIGNED CANNOT BE TICKED — it belongs to somebody. The
// filter can show it (with its owner, admin mode only), never select it.

import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  LinearProgress, MenuItem, TextField, Typography, useTheme,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import {
  getAllUsers, userLabel, userOptionKey, userSearchText, locationsOf, roomsOf, COMMON_ROOM_ID,
} from '../../../core/devices/deviceAdmin';
import { tokenFilter } from '../../../core/utils/search';
import { verdictOfLookup, visibleInScan, SCAN_NEW } from '../../../core/devices/scanVisibility';
import { getLocationWifiList } from '../../../core/api/api';
import { loadNameIndex, invalidateNameIndex } from '../../../core/devices/nameIndex';
import {
  identifyDevice, assignAll, sendWifiAll, watchOnline, checkJobNames,
  newRoomId, roomLabel,
} from '../../../core/install/bleInstall';

const MAX_USERS = 50;
const filterUsers = tokenFilter(userSearchText);
const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);

const STEP = {
  ok: { label: '✓', color: 'success' },
  err: { label: '✗', color: 'error' },
  run: { label: '…', color: 'warning' },
};

function StepChip({ name, state, title }) {
  if (!state) return null;
  const s = STEP[state] || STEP.run;
  return (
    <Chip
      size="small" color={s.color} title={title || ''}
      label={`${s.label} ${name}`}
      sx={{ height: 20, fontSize: 10.5 }}
    />
  );
}

export default function BleInstallPanel({
  devices, serverInfo, refreshServer, mode = 'admin', onRunning,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const { user: me, userId } = useApp();
  const admin = mode === 'admin';

  // ── The job ───────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [owner, setOwner] = useState(admin ? null : { ...me, UserId: userId });
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [newRooms, setNewRooms] = useState([]);   // names typed for this job
  const [roomText, setRoomText] = useState('');
  const [defaultRoom, setDefaultRoom] = useState('');
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [saved, setSaved] = useState([]);         // the location's saved networks

  // ── Rows ──────────────────────────────────────────────────────────────────
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [ticked, setTicked] = useState(() => new Set());
  const [edits, setEdits] = useState({});         // uno -> { name?, roomId? }
  const [status, setStatus] = useState({});       // uno -> { assign, wifi, online, identify, note }
  const [jobUnos, setJobUnos] = useState(() => new Set());   // stay visible after assigning

  const [busy, setBusy] = useState('');           // '' | 'identify' | 'install'
  const [progress, setProgress] = useState(null); // { done, total, label }
  const [error, setError] = useState('');
  const stopRef = useRef(false);
  const onlineStop = useRef(null);

  useEffect(() => () => { if (onlineStop.current) onlineStop.current(); }, []);
  useEffect(() => { if (onRunning) onRunning(!!busy); }, [busy, onRunning]);

  useEffect(() => {
    if (!admin) return;
    getAllUsers().then((r) => setUsers(rowsOf(r))).catch(() => setUsers([]));
  }, [admin]);

  // Location list follows the owner; a single location is picked for you.
  useEffect(() => {
    setLocationId(''); setLocations([]);
    if (!owner) return undefined;
    let alive = true;
    locationsOf(owner.UserId).then((list) => {
      if (!alive) return;
      setLocations(list);
      if (list.length === 1) setLocationId(list[0].LocationID);
    });
    return () => { alive = false; };
  }, [owner]);

  // Rooms + saved networks follow the location.
  useEffect(() => {
    setRooms([]); setNewRooms([]); setDefaultRoom(''); setSaved([]);
    if (!owner || !locationId) return undefined;
    let alive = true;
    roomsOf(owner.UserId, locationId).then((list) => { if (alive) setRooms(list); });
    getLocationWifiList(locationId).then((r) => { if (alive) setSaved(rowsOf(r)); }).catch(() => {});
    return () => { alive = false; };
  }, [owner, locationId]);

  const location = locations.find((l) => String(l.LocationID) === String(locationId));
  const roomOptions = useMemo(() => [
    { id: COMMON_ROOM_ID, label: `Common device (${location?.LocationName || 'location'})` },
    ...rooms.map((r) => ({ id: r.RoomID, label: r.RoomName })),
    ...newRooms.map((n) => ({ id: newRoomId(n), label: `${n} (new)` })),
  ], [rooms, newRooms, location]);

  const addRoom = () => {
    const n = roomText.trim();
    if (!n) return;
    const exists = rooms.find((r) => String(r.RoomName).trim().toLowerCase() === n.toLowerCase());
    const id = exists ? exists.RoomID : newRoomId(n);
    if (!exists && !newRooms.includes(n)) setNewRooms((l) => [...l, n]);
    setDefaultRoom(id);
    setRoomText('');
  };

  // ── Which boards are listed ───────────────────────────────────────────────
  // ⚠️ OWNER MODE LISTS ONLY UNASSIGNED BOARDS, whatever the checkbox says —
  // an assigned board is reached by QR share, never by scan
  // (core/devices/scanVisibility). The checkbox is the admin's.
  const verdictOf = (uno) => verdictOfLookup(serverInfo[uno]);
  const listed = devices.filter((d) => d.uniqueNumber).filter((d) => {
    if (jobUnos.has(d.uniqueNumber)) return true;
    const v = verdictOf(d.uniqueNumber);
    if (!admin) return v != null && visibleInScan(v);
    if (!onlyUnassigned) return true;
    const r = serverInfo[d.uniqueNumber];
    return !(r && r.found && r.assigned);
  });
  // Assigning needs a server row, so a board not on the server yet cannot be ticked.
  const selectable = (uno) => jobUnos.has(uno) || verdictOf(uno) === SCAN_NEW;
  const nameOf = (uno) => edits[uno]?.name ?? (serverInfo[uno]?.device?.DeviceName || '');
  const roomOf = (uno) => {
    const v = edits[uno]?.roomId;
    return v === undefined || v === '' ? defaultRoom : v;
  };
  const patch = (uno, p) => setEdits((e) => ({ ...e, [uno]: { ...(e[uno] || {}), ...p } }));
  const setStep = (uno, p) => setStatus((s) => ({ ...s, [uno]: { ...(s[uno] || {}), ...p } }));
  const toggle = (uno) => setTicked((t) => {
    const n = new Set(t);
    if (n.has(uno)) n.delete(uno); else n.add(uno);
    return n;
  });

  const identify = async (d) => {
    setBusy('identify'); setError('');
    setStep(d.uniqueNumber, { identify: 'run' });
    const r = await identifyDevice(d);
    setStep(d.uniqueNumber, { identify: r.ok ? 'ok' : 'err', note: r.ok ? '' : r.error });
    setBusy('');
  };

  // ── Install ───────────────────────────────────────────────────────────────
  const chosen = listed.filter((d) => ticked.has(d.uniqueNumber) && selectable(d.uniqueNumber));
  const needRoom = chosen.some((d) => roomOf(d.uniqueNumber) === '');
  const whyNot = (() => {
    if (!owner) return 'Choose the owner';
    if (!locationId) return 'Choose the location';
    if (!chosen.length) return 'Tick the boards to install';
    if (needRoom) return 'Choose a default room, or a room on every ticked board';
    if (pass && !ssid.trim()) return 'Enter the Wi-Fi name';
    return '';
  })();

  const install = async () => {
    setError(''); stopRef.current = false;
    const unos = chosen.map((d) => d.uniqueNumber);
    setJobUnos((s) => new Set([...s, ...unos]));

    // Only what is still undone — "Retry failed" is the same button.
    const toAssign = chosen.filter((d) => status[d.uniqueNumber]?.assign !== 'ok');
    const jobRows = toAssign.map((d) => ({
      uno: d.uniqueNumber,
      device: serverInfo[d.uniqueNumber]?.device,
      deviceId: serverInfo[d.uniqueNumber]?.device?.DeviceID,
      name: nameOf(d.uniqueNumber).trim(),
      roomId: roomOf(d.uniqueNumber),
    }));

    setBusy('install');
    try {
      // Alexa names, before anything is written.
      if (jobRows.length) {
        const index = await loadNameIndex(owner.UserId).catch(() => null);
        const problems = checkJobNames(jobRows, { index, rooms, locationName: location?.LocationName });
        if (problems.length) {
          problems.forEach((p) => setStep(p.uno, { assign: 'err', note: p.message }));
          setError(`${problems.length} name${problems.length > 1 ? 's' : ''} would repeat for Alexa — change them and press Install again.`);
          return;
        }
      }

      // 1 — server
      jobRows.forEach((r) => setStep(r.uno, { assign: 'run', note: '' }));
      setProgress({ done: 0, total: jobRows.length, label: 'Assigning on the server' });
      const { results, createdRooms } = await assignAll(jobRows, {
        ownerId: owner.UserId,
        locationId,
        shouldStop: () => stopRef.current,
        onProgress: (m) => setProgress({ done: Object.keys(m).length, total: jobRows.length, label: 'Assigning on the server' }),
      });
      Object.entries(results).forEach(([uno, r]) => setStep(uno, { assign: r.ok ? 'ok' : 'err', note: r.ok ? '' : r.error }));
      invalidateNameIndex();
      // New rooms now exist: re-read the list and point the rows at the real ids.
      if (createdRooms.size) {
        const list = await roomsOf(owner.UserId, locationId).catch(() => rooms);
        setRooms(list);
        setNewRooms([]);
        const fix = (id) => (createdRooms.has(id) ? createdRooms.get(id) : id);
        setDefaultRoom((d) => fix(d));
        setEdits((e) => Object.fromEntries(Object.entries(e).map(([u, v]) => [u, { ...v, roomId: v.roomId === undefined ? v.roomId : fix(v.roomId) }])));
      }

      // 2 — Wi-Fi over Bluetooth, to every ticked board now assigned.
      const assignedNow = chosen.filter((d) => results[d.uniqueNumber]?.ok || status[d.uniqueNumber]?.assign === 'ok');
      const toWifi = ssid.trim()
        ? assignedNow.filter((d) => status[d.uniqueNumber]?.wifi !== 'ok')
        : [];
      if (toWifi.length && !stopRef.current) {
        toWifi.forEach((d) => setStep(d.uniqueNumber, { wifi: 'run' }));
        setProgress({ done: 0, total: toWifi.length, label: 'Sending Wi-Fi over Bluetooth' });
        const wifi = await sendWifiAll(toWifi, {
          ssid: ssid.trim(),
          password: pass,
          locationId,
          shouldStop: () => stopRef.current,
          onProgress: (m) => {
            setProgress({ done: Object.keys(m).length, total: toWifi.length, label: 'Sending Wi-Fi over Bluetooth' });
            Object.entries(m).forEach(([uno, r]) => setStep(uno, { wifi: r.ok ? 'ok' : 'err', note: r.ok ? '' : r.error }));
          },
        });
        const joined = Object.entries(wifi).filter(([, r]) => r.ok).map(([u]) => u);
        // 3 — online: the board's first MQTT message (not a #BLE relay).
        if (joined.length) {
          joined.forEach((u) => setStep(u, { online: 'run' }));
          if (onlineStop.current) onlineStop.current();
          onlineStop.current = watchOnline(
            [...new Set([...joined, ...Object.keys(status).filter((u) => status[u]?.online === 'run')])],
            (u) => setStep(u, { online: 'ok' }),
          );
        }
      }
    } catch (e) {
      setError((e && e.message) || 'Install failed.');
    } finally {
      setBusy('');
      setProgress(null);
      // Every row's server line follows what was just written.
      unos.forEach((u) => refreshServer(u));
    }
  };

  // ── Drawing ───────────────────────────────────────────────────────────────
  const fieldSx = {
    '& .MuiInputBase-input': { color: c.text },
    '& .MuiInputLabel-root': { color: `${c.text}B3` },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: `${c.text}66` },
    '& .MuiSvgIcon-root': { color: c.text },
    '& .MuiFormHelperText-root': { color: `${c.text}99` },
  };
  const doneCount = chosen.filter((d) => status[d.uniqueNumber]?.assign === 'ok'
    && (!ssid.trim() || status[d.uniqueNumber]?.wifi === 'ok')).length;
  const anyFailed = chosen.some((d) => ['err'].includes(status[d.uniqueNumber]?.assign)
    || status[d.uniqueNumber]?.wifi === 'err');

  return (
    <Box sx={{ px: 1, pb: 2 }}>
      {/* ── Job header ── */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1,
      }}
      >
        {admin ? (
          <Autocomplete
            size="small" options={users} value={owner} disabled={!!busy}
            onChange={(_, v) => setOwner(v)}
            getOptionLabel={(u) => userLabel(u)}
            isOptionEqualToValue={(a, b) => userOptionKey(a) === userOptionKey(b)}
            filterOptions={(opts, st) => filterUsers(opts, st).slice(0, MAX_USERS)}
            renderOption={(props, u) => (
              <li {...props} key={userOptionKey(u)}>
                {`${userLabel(u)} · ${u.MobileNumber || 'no mobile'} · ID ${u.UserId}`}
              </li>
            )}
            renderInput={(p) => <TextField {...p} label="Owner" sx={fieldSx} />}
          />
        ) : (
          <TextField size="small" label="Owner" value="You" disabled sx={fieldSx} />
        )}
        <TextField
          select size="small" label="Location (same for all)" value={locationId}
          onChange={(e) => setLocationId(e.target.value)} disabled={!owner || !!busy} sx={fieldSx}
          helperText={owner && !locations.length ? 'No location for this owner — create one in Device Admin.' : ''}
        >
          {locations.map((l) => <MenuItem key={l.LocationID} value={l.LocationID}>{l.LocationName}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Default room" value={defaultRoom}
          onChange={(e) => setDefaultRoom(e.target.value)} disabled={!locationId || !!busy} sx={fieldSx}
        >
          {roomOptions.map((o) => <MenuItem key={String(o.id)} value={o.id}>{o.label}</MenuItem>)}
        </TextField>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small" label="New room" value={roomText} disabled={!locationId || !!busy}
            onChange={(e) => setRoomText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addRoom()}
            sx={{ ...fieldSx, flex: 1 }}
          />
          <Button size="small" variant="outlined" onClick={addRoom} disabled={!roomText.trim() || !!busy} sx={{ color: c.text, borderColor: `${c.text}66` }}>
            Add
          </Button>
        </Box>
        <TextField
          size="small" label="Wi-Fi name" value={ssid} disabled={!!busy}
          onChange={(e) => setSsid(e.target.value)} sx={fieldSx}
          helperText="Leave empty to only assign"
        />
        <TextField
          size="small" label="Wi-Fi password" value={pass} disabled={!!busy}
          onChange={(e) => setPass(e.target.value)} sx={fieldSx}
        />
      </Box>
      {/* The location's saved networks, one tap to fill. */}
      {saved.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {saved.map((w, i) => {
            const n = w.WIFISSID ?? w.SSID ?? w.WifiName;
            const p = w.WIFIPASSWORD ?? w.Password ?? w.WifiPassword ?? '';
            return n ? (
              <Chip
                // eslint-disable-next-line react/no-array-index-key
                key={`${n}-${i}`} size="small" label={n} disabled={!!busy}
                onClick={() => { setSsid(n); setPass(p); }}
                sx={{ height: 22, fontSize: 11, color: c.text, borderColor: `${c.text}66` }} variant="outlined"
              />
            ) : null;
          })}
        </Box>
      )}

      {/* ── Filter + select ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        {admin && (
        <FormControlLabel
          control={(
            <Checkbox
              size="small" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)}
              sx={{ color: c.text, '&.Mui-checked': { color: c.text } }}
            />
          )}
          label={<Typography sx={{ color: c.text, fontSize: 12 }}>Show only unassigned</Typography>}
        />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" disabled={!!busy}
          onClick={() => setTicked(new Set(listed.filter((d) => selectable(d.uniqueNumber)).map((d) => d.uniqueNumber)))}
          sx={{ color: c.text, textTransform: 'none' }}
        >
          Select all
        </Button>
        <Button size="small" disabled={!!busy} onClick={() => setTicked(new Set())} sx={{ color: c.text, textTransform: 'none' }}>
          None
        </Button>
      </Box>

      {/* ── One row per board ── */}
      {listed.map((d) => {
        const uno = d.uniqueNumber;
        const r = serverInfo[uno];
        const dev = r?.device || {};
        const can = selectable(uno);
        const st = status[uno] || {};
        const on = ticked.has(uno);
        let why = '';
        if (!r || r.loading) why = 'looking up on the server…';
        else if (r.error) why = 'server lookup failed';
        else if (!r.found) why = 'not registered on the server';
        else if (r.assigned && !jobUnos.has(uno)) {
          why = admin
            ? `assigned — ${dev.DeviceMasterUser || dev.DeviceMasterUserName || 'an owner'} · ${[dev.LocationName, dev.RoomName].filter(Boolean).join(' / ')}`
            : 'already assigned';
        }
        return (
          <Box
            key={uno}
            sx={{
              border: `1px solid ${c.text}44`, borderRadius: '12px', p: 1, my: 0.75,
              bgcolor: on ? `${c.button}33` : `${c.button}11`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Checkbox
                size="small" checked={on} disabled={!can || !!busy} onChange={() => toggle(uno)}
                sx={{ color: c.text, '&.Mui-checked': { color: c.text }, p: 0.5 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{uno}</Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 11 }}>
                  {r?.found ? `${dev.DeviceTypeName || 'type'} (${dev.DeviceID})` : ''}
                  {d.rssi != null ? ` · ${d.rssi} dBm` : ''}
                  {why ? ` · ${why}` : ''}
                </Typography>
              </Box>
              <Button
                size="small" variant="outlined" disabled={!!busy}
                onClick={() => identify(d)}
                sx={{
                  color: c.text, borderColor: `${c.text}66`, textTransform: 'none', minWidth: 0, px: 1,
                }}
              >
                {st.identify === 'run' ? <CircularProgress size={14} /> : 'Identify'}
              </Button>
            </Box>

            {can && on && (
              <Box sx={{
                display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1,
              }}
              >
                <TextField
                  size="small" label="Device name" value={nameOf(uno)} disabled={!!busy || st.assign === 'ok'}
                  onChange={(e) => patch(uno, { name: e.target.value })}
                  placeholder="e.g. 4 Touch Window Side" sx={fieldSx}
                />
                <TextField
                  select size="small" label="Room" value={edits[uno]?.roomId ?? ''}
                  disabled={!locationId || !!busy || st.assign === 'ok'}
                  onChange={(e) => patch(uno, { roomId: e.target.value })} sx={fieldSx}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">
                    {`Default${defaultRoom !== '' ? ` — ${roomLabel(defaultRoom, rooms, location?.LocationName)}` : ' (none chosen)'}`}
                  </MenuItem>
                  {roomOptions.map((o) => <MenuItem key={String(o.id)} value={o.id}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
            )}

            {(st.assign || st.wifi || st.online || st.identify === 'err') && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                <StepChip name="assigned" state={st.assign} />
                <StepChip name="Wi-Fi" state={st.wifi} />
                <StepChip name={st.online === 'ok' ? 'online' : 'waiting for online'} state={st.online} />
                {st.identify === 'err' && <StepChip name="identify" state="err" />}
                {st.note && <Typography sx={{ color: 'error.main', fontSize: 11 }}>{st.note}</Typography>}
              </Box>
            )}
          </Box>
        );
      })}
      {!listed.length && (
        <Typography sx={{ color: `${c.text}99`, fontSize: 12, p: 1.5, textAlign: 'center' }}>
          {devices.length ? 'No unassigned boards in this scan — untick “Show only unassigned” to see all.' : 'Scan to find boards.'}
        </Typography>
      )}

      {/* ── Run ── */}
      {progress && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: c.text, fontSize: 12 }}>{`${progress.label} — ${progress.done} of ${progress.total}`}</Typography>
          <LinearProgress variant="determinate" value={progress.total ? (100 * progress.done) / progress.total : 0} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{error}</Alert>}
      {chosen.length > 0 && !busy && doneCount === chosen.length && (
        <Alert severity="success" sx={{ mt: 1, py: 0 }}>
          {`All ${doneCount} done${ssid.trim() ? ' — they will show “online” as each joins the Wi-Fi.' : '.'}`}
        </Alert>
      )}
      <Box sx={{
        display: 'flex', gap: 1, mt: 1, alignItems: 'center', position: 'sticky', bottom: 0, py: 1, bgcolor: c.back,
      }}
      >
        <Button
          variant="contained" disabled={!!busy || !!whyNot} onClick={install}
          sx={{
            bgcolor: c.button, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '12px',
          }}
        >
          {busy === 'install' ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : `${anyFailed ? 'Retry failed' : 'Install'} ${chosen.length || ''}`.trim()}
        </Button>
        {busy === 'install' && (
          <Button size="small" onClick={() => { stopRef.current = true; }} sx={{ color: c.text }}>Stop</Button>
        )}
        {!busy && whyNot && <Typography sx={{ color: `${c.text}99`, fontSize: 11 }}>{whyNot}</Typography>}
      </Box>
    </Box>
  );
}
